import { useQuery } from '@tanstack/react-query';
import { api } from '../../services/api.js';

/**
 * 407 (#1370, mock-up screens 6, 8, 9): a rental stays active past its return until it is ended, so the booking page
 * tells staff how late a rental still out is and the car's next booking it affects; on that next booking, that the
 * car is not back; after an end, who ended it and how late. Read from `GET /bookings/:id/late-status`. Nothing is
 * moved: staff contact the customers. Renders nothing when there is nothing to say.
 */
interface LateStatus {
  return_overdue?: boolean;
  late_minutes?: number;
  return_at?: string | null;
  next_booking?: {
    id: string;
    ref: string;
    pickup_at: string;
    customer_name: string | null;
  } | null;
  previous_overdue?: { id: string; ref: string; late_minutes: number } | null;
  ended?: {
    by: 'customer' | 'staff' | null;
    at: string | null;
    late_minutes: number;
    actor_name: string | null;
  } | null;
}

/** "Oct 21, 3:00 PM", Malaysia time, as the mock-up. */
function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

/** 130 → "2 h 10 min", 120 → "2 h", 45 → "45 min". */
export function formatLate(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

export function LateStatusPanel({ bookingId }: { bookingId: string }) {
  const { data } = useQuery({
    queryKey: ['booking-late-status', bookingId],
    queryFn: () => api.get<LateStatus>(`/bookings/${bookingId}/late-status`),
  });
  if (!data) return null;
  const {
    return_overdue,
    late_minutes = 0,
    return_at,
    next_booking,
    previous_overdue,
    ended,
  } = data;
  if (!return_overdue && !next_booking && !previous_overdue && !ended) return null;

  return (
    <div className="flex flex-col gap-2">
      {return_overdue && (
        <div
          data-testid="late-overdue"
          className="rounded-md bg-semantic-danger-bg px-3 py-2 text-sm text-semantic-danger-text"
        >
          <b className="block">Return time passed {formatLate(late_minutes)} ago</b>
          {return_at ? `Return was ${formatTime(return_at)}. ` : ''}The customer has not ended the
          booking.
        </div>
      )}
      {next_booking && (
        <div
          data-testid="late-next-booking"
          className="rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
        >
          <b className="block">Next booking on this car: {formatTime(next_booking.pickup_at)}</b>
          <span className="block">
            {next_booking.ref.toUpperCase()}
            {next_booking.customer_name ? ` · ${next_booking.customer_name}` : ''}
          </span>
          <span className="block">Contact the customers; nothing is moved.</span>
        </div>
      )}
      {previous_overdue && (
        <div
          data-testid="late-car-not-back"
          className="rounded-md bg-semantic-warning-bg px-3 py-2 text-sm text-semantic-warning-text"
        >
          <b className="block">Car not back yet</b>
          The car’s previous rental ({previous_overdue.ref.toUpperCase()}) is{' '}
          {formatLate(previous_overdue.late_minutes)} past its return. Contact the customers;
          nothing is moved.
        </div>
      )}
      {ended && (
        <div
          data-testid="late-ended"
          className="rounded-md bg-semantic-success-bg px-3 py-2 text-sm text-semantic-success-text"
        >
          <b className="block">
            {ended.by === 'staff'
              ? `Ended by staff${ended.actor_name ? ` (${ended.actor_name})` : ''}`
              : 'Ended by the customer'}
            {ended.late_minutes > 0 ? `, ${formatLate(ended.late_minutes)} late` : ''}
          </b>
          {ended.at ? formatTime(ended.at) : ''}
          {ended.late_minutes > 0 && return_at ? ` · return was ${formatTime(return_at)}` : ''}
        </div>
      )}
    </div>
  );
}
